import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRight, ArrowUpRight, Wallet, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { useVendor } from '../context/VendorContext';
import { Header } from '../components/ui/Header';
export function VendorTransactionsScreen() {
  const {
    totalEarnings,
    balance,
    pendingPayout
  } = useVendor();
  const [activeTab, setActiveTab] = useState<'All' | 'Credits' | 'Debits'>('All');
  // Mock transactions
  const transactions = [{
    id: 'TXN-001',
    type: 'credit',
    amount: 800000,
    desc: 'Order Payment - VO-001',
    date: 'Today, 10:30 AM',
    status: 'completed'
  }, {
    id: 'TXN-002',
    type: 'debit',
    amount: 500000,
    desc: 'Withdrawal to Bank',
    date: 'Yesterday, 2:15 PM',
    status: 'completed'
  }, {
    id: 'TXN-003',
    type: 'credit',
    amount: 750000,
    desc: 'Order Payment - VO-002',
    date: 'Mar 15, 2024',
    status: 'completed'
  }, {
    id: 'TXN-004',
    type: 'debit',
    amount: 15000,
    desc: 'Platform Commission',
    date: 'Mar 15, 2024',
    status: 'completed'
  }, {
    id: 'TXN-005',
    type: 'credit',
    amount: 1600000,
    desc: 'Order Payment - VO-003',
    date: 'Mar 12, 2024',
    status: 'completed'
  }, {
    id: 'TXN-006',
    type: 'debit',
    amount: 1000000,
    desc: 'Withdrawal to Bank',
    date: 'Mar 10, 2024',
    status: 'completed'
  }, {
    id: 'TXN-007',
    type: 'credit',
    amount: 450000,
    desc: 'Order Payment - VO-004',
    date: 'Mar 08, 2024',
    status: 'pending'
  }];
  const filteredTransactions = transactions.filter(t => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Credits') return t.type === 'credit';
    if (activeTab === 'Debits') return t.type === 'debit';
    return true;
  });
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10" data-id="element-2525">
      <Header title="Transaction History" showBack data-id="element-2526" />

      {/* Summary Cards */}
      <div className="p-4 grid grid-cols-2 gap-3" data-id="element-2527">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2528">
          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2" data-id="element-2529">
            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-2530" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2531">
            Total Earnings
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2532">
            ₦{(totalEarnings / 1000).toFixed(0)}k
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2533">
          <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-2" data-id="element-2534">
            <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" data-id="element-2535" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2536">
            Pending Payout
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2537">
            ₦{(pendingPayout / 1000).toFixed(0)}k
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-[53px] z-10" data-id="element-2538">
        <div className="flex" data-id="element-2539">
          {['All', 'Credits', 'Debits'].map(tab => <button key={tab} onClick={() => setActiveTab(tab as any)} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2540">
              {tab}
            </button>)}
        </div>
      </div>

      {/* Transaction List */}
      <div className="p-4 space-y-3" data-id="element-2541">
        {filteredTransactions.length === 0 ? <div className="text-center py-12" data-id="element-2542">
            <Wallet className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2543" />
            <h3 className="text-base font-medium text-gray-900 dark:text-white" data-id="element-2544">
              No transactions found
            </h3>
          </div> : filteredTransactions.map((txn, idx) => <motion.div key={txn.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.05
      }} className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-3 active:scale-[0.98] transition-transform cursor-pointer" data-id="element-2545">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${txn.type === 'credit' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`} data-id="element-2546">
                {txn.type === 'credit' ? <ArrowDownRight className="w-5 h-5" data-id="element-2547" /> : <ArrowUpRight className="w-5 h-5" data-id="element-2548" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-2549">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-2550">
                  {txn.desc}
                </p>
                <div className="flex items-center gap-2 mt-1" data-id="element-2551">
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2552">
                    {txn.date}
                  </span>
                  {txn.status === 'pending' && <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded font-medium" data-id="element-2553">
                      Pending
                    </span>}
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-2554">
                <p className={`text-sm font-bold ${txn.type === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`} data-id="element-2555">
                  {txn.type === 'credit' ? '+' : '-'}₦
                  {txn.amount.toLocaleString()}
                </p>
              </div>
            </motion.div>)}
      </div>
    </div>;
}